/**
 * Service API moderne pour les abonnements et tokens
 * Basé sur la documentation API backend mise à jour
 */

import { 
  SubscriptionPlan,
  CustomerTokenBalance,
  TokenTransaction,
  Subscription,
  CreateSubscriptionData,
  GetPlansParams,
  GetTokenTransactionsParams,
  CustomerType,
  BillingPeriod,
  SubscriptionStatus,
  TokenTransactionType,
  convertToLegacyPlan,
  LegacyPricingPlan
} from '../types/subscription';

// Mock data basé sur la structure backend
const MOCK_SME_PLANS: SubscriptionPlan[] = [
  {
    id: 'sme-freemium',
    name: 'PME Freemium',
    description: 'Accès gratuit avec limitations pour découvrir la plateforme',
    customerType: CustomerType.SME,
    billingPeriod: BillingPeriod.MONTHLY,
    monthlyPriceUSD: 0,
    annualPriceUSD: 0,
    annualDiscountPercentage: 0,
    tokenAllocation: {
      monthlyTokens: 100000,
      tokenRollover: false,
      maxRolloverMonths: 0
    },
    features: {
      commercial_management: {
        enabled: true,
        limit: 50,
        description: '50 clients max'
      },
      accounting_basic: {
        enabled: true,
        description: 'Comptabilité de base'
      },
      ai_chat_assistance: {
        enabled: true,
        limit: 10,
        description: '10 requêtes IA par mois'
      }
    },
    isPopular: false,
    isVisible: true,
    sortOrder: 1,
    tags: ['gratuit', 'débutant', 'limitation']
  },
  {
    id: 'sme-standard',
    name: 'PME Standard',
    description: 'Plan complet pour PME avec accès aux demandes de financement',
    customerType: CustomerType.SME,
    billingPeriod: BillingPeriod.MONTHLY,
    monthlyPriceUSD: 20,
    annualPriceUSD: 200,
    annualDiscountPercentage: 16.67,
    tokenAllocation: {
      monthlyTokens: 2000000,
      tokenRollover: true,
      maxRolloverMonths: 3
    },
    features: {
      commercial_management: {
        enabled: true,
        description: 'Gestion commerciale illimitée'
      },
      customer_management: {
        enabled: true,
        description: 'Gestion clients avancée'
      },
      accounting_advanced: {
        enabled: true,
        description: 'Comptabilité avancée'
      },
      financial_reports: {
        enabled: true,
        description: 'Rapports financiers'
      },
      ai_chat_assistance: {
        enabled: true,
        description: 'Assistance IA complète'
      },
      document_analysis: {
        enabled: true,
        description: 'Analyse de documents'
      }
    },
    isPopular: true,
    isVisible: true,
    sortOrder: 2,
    tags: ['recommandé', 'pme', 'complet']
  }
];

const MOCK_FI_PLANS: SubscriptionPlan[] = [
  {
    id: 'fi-freemium',
    name: 'Institution Freemium',
    description: 'Découverte gratuite pour institutions financières',
    customerType: CustomerType.FINANCIAL_INSTITUTION,
    billingPeriod: BillingPeriod.MONTHLY,
    monthlyPriceUSD: 0,
    annualPriceUSD: 0,
    annualDiscountPercentage: 0,
    tokenAllocation: {
      monthlyTokens: 500000,
      tokenRollover: false,
      maxRolloverMonths: 0
    },
    features: {
      lead_prospecting: {
        enabled: true,
        limit: 10,
        description: '10 entreprises max'
      },
      portfolio_management: {
        enabled: false,
        description: 'Non inclus'
      }
    },
    isPopular: false,
    isVisible: true,
    sortOrder: 1,
    tags: ['gratuit', 'limitation']
  },
  {
    id: 'fi-professional',
    name: 'Institution Professionnelle',
    description: 'Solution complète pour institutions financières',
    customerType: CustomerType.FINANCIAL_INSTITUTION,
    billingPeriod: BillingPeriod.MONTHLY,
    monthlyPriceUSD: 100,
    annualPriceUSD: 1000,
    annualDiscountPercentage: 16.67,
    tokenAllocation: {
      monthlyTokens: 10000000,
      tokenRollover: true,
      maxRolloverMonths: 6
    },
    features: {
      lead_prospecting: {
        enabled: true,
        description: 'Prospection illimitée'
      },
      portfolio_management: {
        enabled: true,
        description: 'Gestion de portefeuille'
      },
      multi_user_access: {
        enabled: true,
        limit: 20,
        description: 'Jusqu\'à 20 utilisateurs'
      },
      dedicated_support: {
        enabled: true,
        description: 'Support dédié'
      }
    },
    isPopular: true,
    isVisible: true,
    sortOrder: 2,
    tags: ['recommandé', 'professionnel', 'complet']
  }
];

const MOCK_TOKEN_BALANCE: CustomerTokenBalance = {
  id: 'balance-1',
  customerId: 'user_auth0_mock_id',
  totalTokens: 4880000,
  usedTokens: 1120000,
  remainingTokens: 3760000,
  monthlyAllocation: 2000000,
  rolledOverTokens: 880000,
  bonusTokens: 0,
  currentPeriod: '2025-08',
  periodStartDate: new Date('2025-08-01'),
  periodEndDate: new Date('2025-08-31'),
  rolloverHistory: []
};

const MOCK_SUBSCRIPTION: Subscription = {
  id: 'sub-1',
  customerId: 'user_auth0_mock_id',
  planId: 'sme-standard',
  plan: MOCK_SME_PLANS[1],
  status: SubscriptionStatus.ACTIVE,
  startDate: '2025-06-01T00:00:00Z',
  currentPeriodStart: '2025-08-01T00:00:00Z',
  currentPeriodEnd: '2025-09-01T00:00:00Z',
  cancelAtPeriodEnd: false,
  paymentMethod: {
    type: 'mobile',
    mobileMoneyProvider: 'M-Pesa',
    mobileMoneyNumber: '+243820123456'
  },
  createdAt: '2025-06-01T00:00:00Z',
  updatedAt: '2025-08-01T00:00:00Z'
};

class ModernSubscriptionService {
  // private readonly apiUrl = '/api'; // À utiliser en production pour les vrais appels API

  /**
   * Récupère tous les plans d'abonnement depuis le backend avec fallback vers les mocks
   */
  async getSubscriptionPlans(params?: GetPlansParams): Promise<SubscriptionPlan[]> {
    try {
      // Étape 1: Construire l'URL avec les paramètres
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/land/api/v1';
      const queryParams = new URLSearchParams();
      
      if (params?.customerType) {
        queryParams.set('customerType', params.customerType);
      }
      if (params?.billingPeriod) {
        queryParams.set('billingPeriod', params.billingPeriod);
      }
      
      const url = `${baseUrl}/pricing/plans${queryParams.toString() ? `?${queryParams}` : ''}`;
      
      // Étape 2: Récupérer le token d'authentification
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      
      // Étape 3: Appeler l'API backend
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        // Timeout de 5 secondes
        signal: AbortSignal.timeout(5000)
      });
      
      if (!response.ok) {
        throw new Error(`Backend responded with ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      // Support des deux formats de réponse: { data: [] } ou directement []
      const plans = result.data || result;
      
      if (!Array.isArray(plans)) {
        throw new Error('Invalid response format from backend');
      }
      
      console.log('✅ Plans chargés depuis le backend:', plans.length, 'plan(s)');
      return plans;
      
    } catch (error) {
      // Étape 4: FALLBACK vers les mocks en cas d'erreur
      console.warn('⚠️ Backend indisponible, utilisation des plans par défaut:', error instanceof Error ? error.message : error);
      
      // Utiliser les mocks locaux
      let plans = [...MOCK_SME_PLANS, ...MOCK_FI_PLANS];
      
      // Appliquer le filtrage local pour le fallback
      if (params?.customerType) {
        plans = plans.filter(plan => plan.customerType === params.customerType);
      }
      
      if (params?.billingPeriod) {
        plans = plans.filter(plan => plan.billingPeriod === params.billingPeriod);
      }
      
      console.log('📦 Plans fallback chargés:', plans.length, 'plan(s)');
      return plans;
    }
  }

  /**
   * Récupère un plan spécifique par ID
   */
  async getSubscriptionPlan(planId: string): Promise<SubscriptionPlan | null> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const allPlans = [...MOCK_SME_PLANS, ...MOCK_FI_PLANS];
    return allPlans.find(plan => plan.id === planId) || null;
  }

  /**
   * Récupère l'abonnement actuel de l'utilisateur
   */
  async getCurrentSubscription(): Promise<Subscription | null> {
    await new Promise(resolve => setTimeout(resolve, 400));
    return MOCK_SUBSCRIPTION;
  }

  /**
   * Crée un nouvel abonnement
   */
  async createSubscription(data: CreateSubscriptionData): Promise<Subscription> {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const plan = await this.getSubscriptionPlan(data.planId);
    if (!plan) {
      throw new Error('Plan not found');
    }

    // En production, faire un vrai appel API
    // const response = await fetch(`${this.apiUrl}/subscriptions`, { ... });

    // Simulation de création d'abonnement
    const newSubscription: Subscription = {
      id: `sub-${Date.now()}`,
      customerId: 'user_auth0_mock_id',
      planId: data.planId,
      plan,
      status: SubscriptionStatus.ACTIVE,
      startDate: new Date().toISOString(),
      currentPeriodStart: new Date().toISOString(),
      currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      cancelAtPeriodEnd: false,
      paymentMethod: data.paymentMethod,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    return newSubscription;
  }

  /**
   * Récupère le solde de tokens de l'utilisateur
   */
  async getTokenBalance(): Promise<CustomerTokenBalance> {
    await new Promise(resolve => setTimeout(resolve, 400));
    return MOCK_TOKEN_BALANCE;
  }

  /**
   * Récupère l'historique des transactions de tokens
   */
  async getTokenTransactions(_params?: GetTokenTransactionsParams): Promise<{
    transactions: TokenTransaction[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Mock transactions
    const mockTransactions: TokenTransaction[] = [
      {
        id: 'tx-1',
        customerId: 'user_auth0_mock_id',
        transactionType: TokenTransactionType.ALLOCATION,
        tokenAmount: 2000000,
        balanceBefore: 0,
        balanceAfter: 2000000,
        description: 'Allocation mensuelle - PME Standard',
        createdAt: new Date('2025-08-01')
      },
      {
        id: 'tx-2',
        customerId: 'user_auth0_mock_id',
        transactionType: TokenTransactionType.ALLOCATION,
        tokenAmount: 2000000,
        balanceBefore: 2000000,
        balanceAfter: 4000000,
        description: 'Allocation supplémentaire plan',
        createdAt: new Date('2025-08-05')
      },
      {
        id: 'tx-3',
        customerId: 'user_auth0_mock_id',
        transactionType: TokenTransactionType.USAGE,
        tokenAmount: -50000,
        balanceBefore: 7000000,
        balanceAfter: 6950000,
        description: 'Analyse de document IA',
        featureCode: 'document_analysis',
        createdAt: new Date('2025-08-10')
      }
    ];

    return {
      transactions: mockTransactions,
      pagination: {
        page: 1,
        limit: 20,
        total: mockTransactions.length,
        pages: 1
      }
    };
  }

  // === MÉTHODES DE COMPATIBILITÉ ===
  
  /**
   * Récupère les plans au format legacy pour compatibilité
   * @deprecated Utiliser getSubscriptionPlans à la place
   */
  async getAvailablePlans(): Promise<LegacyPricingPlan[]> {
    const plans = await this.getSubscriptionPlans();
    return plans.map(convertToLegacyPlan);
  }

  /**
   * Données d'abonnement au format legacy
   * @deprecated Utiliser getCurrentSubscription et getTokenBalance à la place
   */
  async getSubscriptionData(userId: string): Promise<{
    id: string;
    userId: string;
    currentPlan: string;
    tokenBalance: number;
    tokenTotal: number;
    paymentHistory: any[];
  }> {
    const [subscription, tokenBalance] = await Promise.all([
      this.getCurrentSubscription(),
      this.getTokenBalance()
    ]);

    return {
      id: subscription?.id || 'mock-sub',
      userId,
      currentPlan: subscription?.plan.name || 'PME',
      tokenBalance: tokenBalance.remainingTokens,
      tokenTotal: tokenBalance.totalTokens,
      paymentHistory: [] // À implémenter si nécessaire
    };
  }
}

// Export singleton
export default new ModernSubscriptionService();
